import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { LoadingButton } from '@mui/lab';
import { Button as AntdButton, Form, Input, Modal } from 'antd';
import { Rule } from 'antd/es/form';
import clsx from 'clsx';
import LunarDatePicker from 'components/LunarDatePicker/LunarDatePicker';
import { setSelectedMenuKeys } from 'features/appUI/slice';
import { useAppDispatch } from 'lib/stores/hook';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { getPath } from 'routes/router-paths';
import { messageUtils } from 'utils';
import { MODULE_TYPE } from 'utils/constants';
import {
  FIELD_TYPE,
  startDateAndEndDateValidator,
} from 'utils/moment/startDateAndEndDateValidator';
import {
  formatHolidayConfigFormValuesToPayload,
  formatPeaksResponseToFormValues,
} from './helper';
import {
  useDeletePeakMutation,
  useGetPeaksQuery,
  useUpdatePeakMutation,
} from 'features/configuration/query';
import {
  PEAK_SEASON_TYPE,
  THolidayConfigFormType,
} from 'features/configuration/types';
import { FORM_FIELD, FormLabels, FormValidations } from './constants';
import { PEAK_SEASON_CONFIG_TYPE } from 'modules/Configuration/PeakSeasonConfigPage/constants';

const { useForm } = Form;
const { confirm } = Modal;

const HolidayConfiguration = () => {
  const dispatch = useAppDispatch();
  const { message } = messageUtils;

  const [form] = useForm();
  const { setFieldsValue, getFieldValue } = form;

  const { data: getLunarPeaksResponse } = useGetPeaksQuery({
    type: PEAK_SEASON_TYPE.LUNAR_HOLIDAY,
  });

  const { data: getSolarPeaksResponse } = useGetPeaksQuery({
    type: PEAK_SEASON_TYPE.SOLAR_HOLIDAY,
  });

  const [updatePeakMutationFn, { isLoading }] = useUpdatePeakMutation();

  const [deletePeakMutationFn, { isLoading: isDeleting }] =
    useDeletePeakMutation();

  const initialValues = useMemo(() => {
    return {
      [FORM_FIELD.LUNAR_DAYS]: formatPeaksResponseToFormValues(
        getLunarPeaksResponse?.data ?? []
      ),
      [FORM_FIELD.SOLAR_DAYS]: formatPeaksResponseToFormValues(
        getSolarPeaksResponse?.data ?? []
      ),
    };
  }, [getLunarPeaksResponse?.data, getSolarPeaksResponse?.data]);

  const handleDeletePeak = useCallback(
    async (
      peakCode: string,
      formListIndex: number,
      onRemove: (index: number) => void
    ) => {
      try {
        await deletePeakMutationFn({
          code: peakCode,
        }).unwrap();
        void message.success(`Xóa cao điểm thành công`);
        onRemove(formListIndex);
      } catch (e) {}
    },
    [deletePeakMutationFn, message]
  );

  const handleDeleteFormListItem = useCallback(
    (fieldName: string, index: number, onRemove: (index: number) => void) => {
      const record = getFieldValue([fieldName, index]);

      const { code } = record || {};
      if (!code) {
        onRemove(index);
        return;
      }

      confirm({
        title: (
          <div className="ant-modal-confirm-heading">
            <ErrorIcon className="icon-danger" />
            <span>Cảnh báo</span>
          </div>
        ),
        icon: null,
        content: (
          <p className="mb-4">Bạn có chắc chắn muốn xóa cao điểm này?</p>
        ),
        onOk: () => handleDeletePeak(code, index, onRemove),
        okButtonProps: {
          disabled: isDeleting,
          danger: true,
        },
        cancelButtonProps: {
          className:
            '!border !border-solid !border-error !text-error hover:opacity-75',
        },
        centered: true,
      });
    },
    [getFieldValue, handleDeletePeak, isDeleting]
  );

  const getFormItem = useCallback(
    (fieldName: string) => (
      <Form.List name={fieldName}>
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, name) => (
              <div key={field.key} className="mb-4 flex items-start gap-x-4">
                <Form.Item
                  name={[name, FORM_FIELD.CODE]}
                  label={FormLabels[FORM_FIELD.CODE]}
                  rules={FormValidations[FORM_FIELD.CODE]}
                  className="hidden"
                >
                  <Input placeholder={FormLabels[FORM_FIELD.CODE]} />
                </Form.Item>
                <Form.Item
                  name={[name, FORM_FIELD.NAME]}
                  label={FormLabels[FORM_FIELD.NAME]}
                  rules={FormValidations[FORM_FIELD.NAME]}
                  className="mb-0 flex-1"
                >
                  <Input placeholder={FormLabels[FORM_FIELD.NAME]} />
                </Form.Item>

                <Form.Item
                  name={[name, FORM_FIELD.START_FROM]}
                  label={FormLabels[FORM_FIELD.START_FROM]}
                  className="mb-0 flex-1"
                  rules={[
                    ...(FormValidations[FORM_FIELD.START_FROM] ?? []),
                    startDateAndEndDateValidator(
                      [fieldName, name, FORM_FIELD.END_TO],
                      FIELD_TYPE.START_DATE
                    ) as Rule,
                  ]}
                >
                  <LunarDatePicker />
                </Form.Item>

                <Form.Item
                  name={[name, FORM_FIELD.END_TO]}
                  label={FormLabels[FORM_FIELD.END_TO]}
                  className="mb-0 flex-1"
                  rules={[
                    ...(FormValidations[FORM_FIELD.END_TO] ?? []),
                    startDateAndEndDateValidator(
                      [fieldName, name, FORM_FIELD.START_FROM],
                      FIELD_TYPE.END_DATE
                    ) as Rule,
                  ]}
                >
                  <LunarDatePicker />
                </Form.Item>

                <CloseIcon
                  onClick={() =>
                    handleDeleteFormListItem(fieldName, name, remove)
                  }
                  className="mt-8.5 cursor-pointer text-xl text-gray-500 hover:text-error"
                />
              </div>
            ))}
            <AntdButton
              type="dashed"
              block
              onClick={() => add()}
              className={clsx(
                'flex items-center justify-center gap-x-2 shadow-none',
                fields.length && 'mt-4'
              )}
              icon={<AddCircleOutlineOutlinedIcon className="text-xl" />}
            >
              Thêm
            </AntdButton>
          </>
        )}
      </Form.List>
    ),
    [handleDeleteFormListItem]
  );

  const onSubmit = useCallback(
    async (values: THolidayConfigFormType) => {
      try {
        const payload = formatHolidayConfigFormValuesToPayload(values);

        if (!payload?.length) return;

        await updatePeakMutationFn(payload).unwrap();
        void message.success('Lưu thành công');
      } catch (e) {}
    },
    [updatePeakMutationFn, message]
  );

  useEffect(() => {
    dispatch(
      setSelectedMenuKeys([
        getPath(
          'peakSeasonConfigPage',
          MODULE_TYPE.HOTEL,
          PEAK_SEASON_CONFIG_TYPE.PEAK_SEASON
        ),
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    setFieldsValue(initialValues);
  }, [initialValues, setFieldsValue]);

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <div className="flex flex-col gap-y-4">
        <section className="flex-1 px-2">
          <h2 className="mb-4 text-base font-semibold text-primary">
            {FormLabels[FORM_FIELD.SOLAR_DAYS]}
          </h2>

          {getFormItem(FORM_FIELD.SOLAR_DAYS)}
        </section>

        <section className="flex-1 px-2">
          <h2 className="mb-4 text-base font-semibold text-primary">
            {FormLabels[FORM_FIELD.LUNAR_DAYS]}
          </h2>

          {getFormItem(FORM_FIELD.LUNAR_DAYS)}
        </section>

        <div className="mt-3 text-right">
          <LoadingButton variant="contained" type="submit" loading={isLoading}>
            Lưu
          </LoadingButton>
        </div>
      </div>
    </Form>
  );
};

export default memo(HolidayConfiguration);
